import React from 'react'
import { Box, RoundIconButton, Stack } from '@ui'
import { vars } from 'ui/styles/vars.css'

type MicControlButtonProps = {
    isMuted: boolean
    onToggleMute: (e: React.MouseEvent) => void
    onSettingsClick?: () => void
    /** Additional props to spread on the container (e.g. triggerProps from CardOpener) */
    containerProps?: Omit<React.HTMLAttributes<HTMLElement>, 'color'>
}

/**
 * Microphone control button with mute toggle and settings dots.
 * Used in both desktop (LiveSessionFooter) and mobile (MobileLiveAudioBar) live UI.
 */
export const MicControlButton = ({
    isMuted,
    onToggleMute,
    onSettingsClick,
    containerProps,
}: MicControlButtonProps) => {
    // Show settings dots only if there's a click handler or containerProps (CardOpener trigger)
    const hasSettingsAction = !!onSettingsClick || !!containerProps

    return (
        <Box
            horizontal
            rounded
            background="level2"
            tint={isMuted ? 'red' : undefined}
            width={hasSettingsAction ? 'x10' : undefined}
            cursor="pointer"
            overflow="hidden"
            aria-label="Microphone settings"
            {...containerProps}
        >
            {hasSettingsAction && (
                <Stack
                    horizontal
                    grow
                    hoverable
                    centerContent
                    rounded
                    paddingLeft="xxs"
                    background="level2"
                    tint={isMuted ? 'red' : undefined}
                    paddingRight="md"
                    insetRight="sm"
                    onClick={onSettingsClick}
                >
                    <ThreeDots />
                </Stack>
            )}
            <RoundIconButton
                icon={isMuted ? 'microphoneOff' : 'microphone'}
                label={isMuted ? 'Turn On Mic' : 'Turn Off Mic'}
                background="level3"
                tint={isMuted ? 'red' : undefined}
                boxShadow="medium"
                onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onToggleMute(e)
                }}
            />
        </Box>
    )
}

const dotArray = Array.from({ length: 3 }).map((_, index) => `dot-${index}`)

const ThreeDots = () => {
    return (
        <Stack horizontal grow centerContent gap="xxs">
            {dotArray.map((key) => (
                <Box
                    key={key}
                    rounded="full"
                    style={{
                        width: 4,
                        height: 4,
                        background: vars.color.foreground.gray2,
                    }}
                />
            ))}
        </Stack>
    )
}
